import { Injectable } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import NoSleep from 'nosleep.js';
import {
    AudioChunkUploadUrlRequest,
    ConsultationsService,
} from '../consultations/consultations.service';
import {
    SCREEN_WAKE_LOCK_VIDEO_MP4,
    SCREEN_WAKE_LOCK_VIDEO_WEBM,
} from './screen-wake-lock-media';

const AUDIO_MIME_TYPE = 'audio/webm; codecs=opus';
const AUDIO_CHUNK_INTERVAL_MS = 10_000;
const SCREEN_WAKE_LOCK_RETRY_MS = 10_000;
const SCREEN_WAKE_LOCK_PRIME_TIMEOUT_MS = 60_000;
const AUDIO_ISSUER = 'doctor';

export type AudioCaptureInterruptionReason =
    | 'track-ended'
    | 'track-muted'
    | 'recorder-error'
    | 'app-hidden'
    | 'app-pagehide'
    | 'app-freeze'
    | 'device-change'
    | 'permission-revoked'
    | 'microphone-unavailable';

export interface AudioCaptureInterruption {
    reason: AudioCaptureInterruptionReason;
    title: string;
    message: string;
    occurredAt: number;
}

interface AudioChunkMetadata {
    isPaused: boolean;
    timestamp: number;
    totalPausedTime: number;
}

interface QueuedAudioChunk {
    blob: Blob;
    isPaused: boolean;
    timestamp: number;
    totalPausedTime: number;
}

export class StartRecordingError extends Error {
    constructor(
        readonly toastTitle: string,
        readonly toastMessage: string,
    ) {
        super(toastMessage);
    }
}

@Injectable()
export class RecordingSessionService {

    constructor(
        private readonly consultationsService: ConsultationsService,
    ) {}

    private readonly audioCaptureInterruptionSubject = new Subject<AudioCaptureInterruption>();
    private mediaRecorder: MediaRecorder | null = null;
    private mediaStream: MediaStream | null = null;
    private activeConsultationId: string | null = null;
    private pendingAudioUploads: Promise<void>[] = [];
    private pendingAudioRecorderStops: Promise<void>[] = [];
    private audioUploadFailure: unknown = null;
    private audioChunkTimerId: ReturnType<typeof setTimeout> | null = null;
    private audioChunkMetadataByRecorder = new WeakMap<MediaRecorder, AudioChunkMetadata>();
    private shouldContinueAudioRecording = false;
    private isPausedByAudioCaptureInterruption = false;
    private lastAudioChunkTimestamp = 0;
    private pausedStartedAtMs: number | null = null;
    private startedAtMs = 0;
    private totalPausedTimeMs = 0;
    private mediaStreamListenerCleanups: Array<() => void> = [];
    private audioCaptureLifecycleCleanups: Array<() => void> = [];
    private noSleep: NoSleep | null = null;
    private isScreenWakeLockEnabled = false;
    private isScreenWakeLockRequestInFlight = false;
    private screenWakeLockRetryTimerId: ReturnType<typeof setTimeout> | null = null;
    private screenWakeLockPrimeTimerId: ReturnType<typeof setTimeout> | null = null;
    private lowPowerKeepAwakeVideo: HTMLVideoElement | null = null;
    private lastAudioTimeByIssuer: Record<typeof AUDIO_ISSUER, string> = {
        doctor: '',
    };
    private audioUploadChainByIssuer: Record<typeof AUDIO_ISSUER, Promise<void>> = {
        doctor: Promise.resolve(),
    };

    get isRecording(): boolean {
        return this.mediaRecorder?.state === 'recording';
    }

    get isPaused(): boolean {
        return this.mediaRecorder?.state === 'paused' || this.pausedStartedAtMs !== null;
    }

    get hasLocalRecorder(): boolean {
        return (
            this.shouldContinueAudioRecording
            && (!!this.mediaRecorder || this.isPausedByAudioCaptureInterruption)
        );
    }

    get audioCaptureInterruption$(): Observable<AudioCaptureInterruption> {
        return this.audioCaptureInterruptionSubject.asObservable();
    }

    get elapsedRecordingSeconds(): number {
        if (!this.startedAtMs) {
            return 0;
        }

        const currentPauseMs = this.pausedStartedAtMs ? Date.now() - this.pausedStartedAtMs : 0;
        const elapsedMs = Date.now() - this.startedAtMs - this.totalPausedTimeMs - currentPauseMs;

        return Math.max(0, Math.floor(elapsedMs / 1000));
    }

    prepareScreenWakeLockFromUserGesture(): void {
        // Runs inside a user gesture (tap on "Iniciar gravação"), which iOS
        // requires before a <video> may start playing. Priming here guarantees
        // the keep-awake mechanism is already active by the time the async audio
        // capture and consultation requests finish.
        this.clearScreenWakeLockPrimeTimer();
        void this.acquireScreenWakeLock(true);

        // If a recording never actually starts keeping the screen awake, release
        // whatever we primed so we don't hold the display on indefinitely.
        this.screenWakeLockPrimeTimerId = setTimeout(() => {
            this.screenWakeLockPrimeTimerId = null;

            if (!this.shouldKeepScreenAwake()) {
                this.releaseScreenWakeLock();
            }
        }, SCREEN_WAKE_LOCK_PRIME_TIMEOUT_MS);
    }

    async startNewConsultationRecording(): Promise<string> {
        let preparedMediaRecorder: MediaRecorder | null = null;
        let startedConsultationId: string | null = null;

        try {
            preparedMediaRecorder = await this.prepareAudioRecording();
            const consultationId = await firstValueFrom(
                this.consultationsService.startNewConsultation(),
            );

            startedConsultationId = consultationId;
            this.startAudioRecording(consultationId, preparedMediaRecorder);
            preparedMediaRecorder = null;

            return consultationId;
        } catch (error) {
            this.stopPreparedMediaRecorder(preparedMediaRecorder);
            await this.clearStartedConsultation(startedConsultationId);
            throw error;
        }
    }

    async resumeConsultationRecording(consultationId: string): Promise<void> {
        if (this.activeConsultationId === consultationId && this.shouldContinueAudioRecording) {
            return;
        }

        let preparedMediaRecorder: MediaRecorder | null = null;

        try {
            preparedMediaRecorder = await this.prepareAudioRecording();
            this.consultationsService.setConsultationId(consultationId);
            this.startAudioRecording(consultationId, preparedMediaRecorder);
            preparedMediaRecorder = null;
        } catch (error) {
            this.stopPreparedMediaRecorder(preparedMediaRecorder);
            throw error;
        }
    }

    async togglePause(): Promise<boolean> {
        if (!this.mediaRecorder) {
            if (this.isPausedByAudioCaptureInterruption) {
                await this.resumePausedAudioRecording();
                return false;
            }

            return this.isPaused;
        }

        if (this.mediaRecorder.state === 'recording') {
            this.pauseCurrentAudioRecording();
            this.isPausedByAudioCaptureInterruption = false;
            return true;
        }

        if (this.mediaRecorder.state === 'paused') {
            await this.resumePausedAudioRecording();
            return false;
        }

        if (this.isPausedByAudioCaptureInterruption) {
            await this.resumePausedAudioRecording();
            return false;
        }

        return this.isPaused;
    }

    async finishConsultation(consultationId: string): Promise<void> {
        await this.stopAudioRecording();
        await this.waitForPendingAudioUploads();
        await firstValueFrom(this.consultationsService.finishConsultation(consultationId));
        this.resetSession();
    }

    destroyLocalRecorder(): void {
        this.stopPreparedMediaRecorder(this.mediaRecorder);
        this.stopMediaStream();
        this.mediaRecorder = null;
    }

    private async prepareAudioRecording(): Promise<MediaRecorder> {
        if (!navigator.mediaDevices?.getUserMedia) {
            throw new StartRecordingError(
                'Microfone indisponível',
                'Este navegador não permite acessar o microfone neste dispositivo.',
            );
        }

        if (!MediaRecorder.isTypeSupported(AUDIO_MIME_TYPE)) {
            throw new StartRecordingError(
                'Gravação não suportada',
                'Este navegador não suporta o formato de áudio necessário para iniciar a consulta.',
            );
        }

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const hasLiveAudioTrack = stream
            .getAudioTracks()
            .some((track) => track.readyState === 'live');

        if (!hasLiveAudioTrack) {
            this.stopMediaStreamTracks(stream);
            throw new StartRecordingError(
                'Microfone não encontrado',
                'Nenhum microfone conectado foi encontrado. Conecte um microfone e tente iniciar novamente.',
            );
        }

        try {
            return this.createMediaRecorder(stream);
        } catch (error) {
            this.stopMediaStreamTracks(stream);
            throw error;
        }
    }

    private startAudioRecording(consultationId: string, mediaRecorder: MediaRecorder): void {
        this.pendingAudioUploads = [];
        this.pendingAudioRecorderStops = [];
        this.audioUploadFailure = null;
        this.totalPausedTimeMs = 0;
        this.pausedStartedAtMs = null;
        this.startedAtMs = Date.now();
        this.shouldContinueAudioRecording = true;
        this.isPausedByAudioCaptureInterruption = false;
        this.lastAudioChunkTimestamp = 0;
        this.lastAudioTimeByIssuer.doctor = '';
        this.audioUploadChainByIssuer.doctor = Promise.resolve();
        this.activeConsultationId = consultationId;
        this.mediaStream = mediaRecorder.stream;

        this.watchAudioCaptureLifecycle();
        this.watchMediaStream(mediaRecorder.stream);
        this.startAudioChunkRecorder(consultationId, mediaRecorder);
        this.enableScreenWakeLock();
    }

    private startAudioChunkRecorder(consultationId: string, mediaRecorder: MediaRecorder): void {
        mediaRecorder.addEventListener('dataavailable', (event: BlobEvent) => {
            if (!event.data.size) {
                return;
            }

            const metadata = this.getAudioChunkMetadata(mediaRecorder);

            this.enqueueAudioChunkUpload(consultationId, {
                blob: event.data,
                ...metadata,
            });
        });

        mediaRecorder.addEventListener('error', () => {
            this.handleAudioCaptureInterruption('recorder-error');
        });

        mediaRecorder.addEventListener('stop', () => {
            if (!this.shouldContinueAudioRecording || this.mediaStream !== mediaRecorder.stream) {
                return;
            }

            if (
                this.isPausedByAudioCaptureInterruption
                || !this.hasLiveAudioTrack(mediaRecorder.stream)
            ) {
                return;
            }

            this.startAudioChunkRecorder(
                consultationId,
                this.createMediaRecorder(mediaRecorder.stream),
            );
        }, { once: true });

        this.mediaRecorder = mediaRecorder;
        mediaRecorder.start();
        this.scheduleAudioChunkRotation(mediaRecorder);
    }

    private async clearStartedConsultation(consultationId: string | null): Promise<void> {
        if (!consultationId) {
            return;
        }

        try {
            await firstValueFrom(this.consultationsService.finishConsultation(consultationId));
        } catch {
            this.consultationsService.clearConsultationId();
        }

        this.resetSession();
    }

    private async stopAudioRecording(): Promise<void> {
        const mediaRecorder = this.mediaRecorder;

        this.shouldContinueAudioRecording = false;
        this.clearAudioChunkTimer();
        this.disableScreenWakeLock();

        if (!mediaRecorder || mediaRecorder.state === 'inactive') {
            await this.waitForPendingAudioRecorderStops();
            this.stopMediaStream();
            return;
        }

        await this.stopAudioRecorderForChunk(mediaRecorder);
        await this.waitForPendingAudioRecorderStops();
        this.stopMediaStream();
        this.mediaRecorder = null;
        this.pausedStartedAtMs = null;
    }

    private enqueueAudioChunkUpload(consultationId: string, chunk: QueuedAudioChunk): void {
        const upload = this.audioUploadChainByIssuer[AUDIO_ISSUER].then(() =>
            this.uploadAudioChunk(consultationId, chunk),
        );

        this.audioUploadChainByIssuer[AUDIO_ISSUER] = upload.catch(() => void 0);
        this.pendingAudioUploads.push(upload);

        void upload.catch((error) => {
            this.audioUploadFailure ??= error;
            console.error('Audio chunk upload failed.', error);
        });
    }

    private async uploadAudioChunk(
        consultationId: string,
        chunk: QueuedAudioChunk,
    ): Promise<void> {
        const payload: AudioChunkUploadUrlRequest = {
            issuer: AUDIO_ISSUER,
            timestamp: chunk.timestamp,
            contentType: AUDIO_MIME_TYPE,
            lastAudioTime: this.lastAudioTimeByIssuer[AUDIO_ISSUER],
            totalPausedTime: chunk.totalPausedTime,
            isPaused: chunk.isPaused,
        };

        const { signedUrl, headers } = await firstValueFrom(
            this.consultationsService.createAudioChunkUploadUrl(consultationId, payload),
        );

        await firstValueFrom(
            this.consultationsService.uploadAudioChunkToSignedUrl(signedUrl, headers, chunk.blob),
        );

        this.lastAudioTimeByIssuer[AUDIO_ISSUER] = String(chunk.timestamp);
    }

    private async waitForPendingAudioUploads(): Promise<void> {
        const uploads = [...this.pendingAudioUploads];
        this.pendingAudioUploads = [];

        const results = await Promise.allSettled(uploads);
        const failedUpload = results.find(
            (result): result is PromiseRejectedResult => result.status === 'rejected',
        );

        if (failedUpload) {
            throw failedUpload.reason;
        }

        if (this.audioUploadFailure) {
            throw this.audioUploadFailure;
        }
    }

    private resetSession(): void {
        this.startedAtMs = 0;
        this.totalPausedTimeMs = 0;
        this.pausedStartedAtMs = null;
        this.shouldContinueAudioRecording = false;
        this.isPausedByAudioCaptureInterruption = false;
        this.activeConsultationId = null;
        this.clearAudioChunkTimer();
        this.clearAudioCaptureLifecycleWatchers();
        this.clearMediaStreamWatchers();
        this.disableScreenWakeLock();
        this.pendingAudioRecorderStops = [];
        this.lastAudioChunkTimestamp = 0;
        this.pendingAudioUploads = [];
        this.audioUploadFailure = null;
        this.lastAudioTimeByIssuer.doctor = '';
        this.audioUploadChainByIssuer.doctor = Promise.resolve();
        this.destroyLocalRecorder();
    }

    private stopMediaStream(): void {
        this.clearMediaStreamWatchers();

        const mediaStream = this.mediaStream;
        this.mediaStream = null;

        if (mediaStream) {
            this.stopMediaStreamTracks(mediaStream);
        }
    }

    private stopPreparedMediaRecorder(mediaRecorder: MediaRecorder | null): void {
        if (!mediaRecorder) {
            return;
        }

        if (this.mediaRecorder === mediaRecorder) {
            this.shouldContinueAudioRecording = false;
            this.clearAudioChunkTimer();
        }

        if (mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }

        this.stopMediaStreamTracks(mediaRecorder.stream);

        if (this.mediaRecorder === mediaRecorder) {
            this.mediaRecorder = null;
        }

        if (this.mediaStream === mediaRecorder.stream) {
            this.mediaStream = null;
        }
    }

    private stopMediaStreamTracks(stream: MediaStream): void {
        stream.getTracks().forEach((track) => track.stop());
    }

    private createMediaRecorder(stream: MediaStream): MediaRecorder {
        return new MediaRecorder(stream, {
            mimeType: AUDIO_MIME_TYPE,
        });
    }

    private async resumePausedAudioRecording(): Promise<void> {
        if (!this.activeConsultationId || !this.shouldContinueAudioRecording) {
            return;
        }

        const mediaRecorder = this.mediaRecorder;

        if (
            mediaRecorder?.state === 'paused'
            && this.hasUsableAudioTrack(mediaRecorder.stream)
        ) {
            mediaRecorder.resume();
            this.finishCurrentPause();
            this.isPausedByAudioCaptureInterruption = false;
            this.scheduleAudioChunkRotation(mediaRecorder);
            void this.acquireScreenWakeLock();
            return;
        }

        const replacementRecorder = await this.prepareAudioRecording();

        this.stopMediaStream();
        this.mediaRecorder = null;
        this.mediaStream = replacementRecorder.stream;
        this.watchMediaStream(replacementRecorder.stream);
        this.finishCurrentPause();
        this.isPausedByAudioCaptureInterruption = false;
        this.startAudioChunkRecorder(this.activeConsultationId, replacementRecorder);
        void this.acquireScreenWakeLock();
    }

    private pauseCurrentAudioRecording(): void {
        this.clearAudioChunkTimer();

        if (this.mediaRecorder?.state === 'recording') {
            try {
                this.mediaRecorder.pause();
            } catch {
                // Some mobile browsers can transition the recorder while an external interruption is firing.
            }
        }

        this.pausedStartedAtMs ??= Date.now();
        this.releaseScreenWakeLock();
    }

    private finishCurrentPause(): void {
        if (this.pausedStartedAtMs) {
            this.totalPausedTimeMs += Date.now() - this.pausedStartedAtMs;
            this.pausedStartedAtMs = null;
        }
    }

    private handleAudioCaptureInterruption(reason: AudioCaptureInterruptionReason): void {
        if (!this.shouldContinueAudioRecording) {
            return;
        }

        if (this.isPausedByAudioCaptureInterruption) {
            return;
        }

        this.pauseCurrentAudioRecording();
        this.isPausedByAudioCaptureInterruption = true;

        const mediaRecorder = this.mediaRecorder;

        if (
            mediaRecorder
            && (mediaRecorder.state === 'recording' || !this.hasLiveAudioTrack(mediaRecorder.stream))
        ) {
            void this.stopAudioRecorderForChunk(mediaRecorder);
        }

        this.audioCaptureInterruptionSubject.next({
            reason,
            title: 'Consulta pausada',
            message: this.getAudioCaptureInterruptionMessage(reason),
            occurredAt: Date.now(),
        });
    }

    private getAudioCaptureInterruptionMessage(
        reason: AudioCaptureInterruptionReason,
    ): string {
        if (
            reason === 'app-hidden'
            || reason === 'app-pagehide'
            || reason === 'app-freeze'
        ) {
            return 'A captação de áudio foi pausada porque o app perdeu foco ou foi suspenso. Retome manualmente quando voltar à consulta.';
        }

        if (reason === 'permission-revoked') {
            return 'A permissão do microfone mudou durante a consulta. Libere o microfone e retome manualmente para continuar.';
        }

        return 'Detectamos uma interrupção no microfone. A consulta foi pausada com segurança; retome manualmente quando o áudio estiver disponível.';
    }

    private watchMediaStream(stream: MediaStream): void {
        this.clearMediaStreamWatchers();

        stream.getAudioTracks().forEach((track) => {
            const handleEnded = (): void => this.handleAudioCaptureInterruption('track-ended');
            const handleMute = (): void => this.handleAudioCaptureInterruption('track-muted');

            track.addEventListener('ended', handleEnded);
            track.addEventListener('mute', handleMute);

            this.mediaStreamListenerCleanups.push(() => {
                track.removeEventListener('ended', handleEnded);
                track.removeEventListener('mute', handleMute);
            });
        });
    }

    private clearMediaStreamWatchers(): void {
        this.mediaStreamListenerCleanups.forEach((cleanup) => cleanup());
        this.mediaStreamListenerCleanups = [];
    }

    private watchAudioCaptureLifecycle(): void {
        this.clearAudioCaptureLifecycleWatchers();

        const addDocumentListener = (eventName: string, handler: EventListener): void => {
            document.addEventListener(eventName, handler);
            this.audioCaptureLifecycleCleanups.push(() =>
                document.removeEventListener(eventName, handler),
            );
        };

        addDocumentListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.handleAudioCaptureInterruption('app-hidden');
                return;
            }

            void this.acquireScreenWakeLock();
        });

        const handleUserActivation = (): void => {
            // Re-prime the keep-awake mechanism on every interaction during an
            // active recording. iOS requires a user gesture to (re)start the
            // fallback video, and this handler runs inside one.
            if (this.shouldKeepScreenAwake()) {
                void this.acquireScreenWakeLock(true);
            }
        };

        window.addEventListener('pointerdown', handleUserActivation, { capture: true });
        window.addEventListener('touchstart', handleUserActivation, { capture: true });
        this.audioCaptureLifecycleCleanups.push(() => {
            window.removeEventListener('pointerdown', handleUserActivation, { capture: true });
            window.removeEventListener('touchstart', handleUserActivation, { capture: true });
        });

        addDocumentListener('freeze', () => this.handleAudioCaptureInterruption('app-freeze'));
        const handlePageHide = (): void => this.handleAudioCaptureInterruption('app-pagehide');
        window.addEventListener('pagehide', handlePageHide);
        this.audioCaptureLifecycleCleanups.push(() =>
            window.removeEventListener('pagehide', handlePageHide),
        );

        if (navigator.mediaDevices?.addEventListener) {
            const handleDeviceChange = (): void => {
                if (!this.hasLiveAudioTrack(this.mediaStream)) {
                    this.handleAudioCaptureInterruption('device-change');
                }
            };

            navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
            this.audioCaptureLifecycleCleanups.push(() =>
                navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange),
            );
        }

        this.watchMicrophonePermission();
    }

    private watchMicrophonePermission(): void {
        if (!navigator.permissions?.query) {
            return;
        }

        void navigator.permissions
            .query({ name: 'microphone' as PermissionName })
            .then((permissionStatus) => {
                if (!this.shouldContinueAudioRecording) {
                    return;
                }

                const handlePermissionChange = (): void => {
                    if (permissionStatus.state === 'denied') {
                        this.handleAudioCaptureInterruption('permission-revoked');
                    }
                };

                permissionStatus.addEventListener('change', handlePermissionChange);
                this.audioCaptureLifecycleCleanups.push(() =>
                    permissionStatus.removeEventListener('change', handlePermissionChange),
                );
                handlePermissionChange();
            })
            .catch(() => void 0);
    }

    private clearAudioCaptureLifecycleWatchers(): void {
        this.audioCaptureLifecycleCleanups.forEach((cleanup) => cleanup());
        this.audioCaptureLifecycleCleanups = [];
    }

    private enableScreenWakeLock(): void {
        this.isScreenWakeLockEnabled = true;
        void this.acquireScreenWakeLock();
    }

    private disableScreenWakeLock(): void {
        this.isScreenWakeLockEnabled = false;
        this.clearScreenWakeLockRetry();
        this.clearScreenWakeLockPrimeTimer();
        this.releaseScreenWakeLock();
    }

    private getNoSleep(): NoSleep {
        return (this.noSleep ??= new NoSleep());
    }

    private async acquireScreenWakeLock(force = false): Promise<void> {
        if (!force && !this.shouldKeepScreenAwake()) {
            return;
        }

        if (this.isScreenWakeLockRequestInFlight) {
            return;
        }

        this.isScreenWakeLockRequestInFlight = true;

        try {
            // nosleep.js uses the native Wake Lock API when available
            // (Android, iOS Safari 16.4+) and a real looping <video> otherwise
            // (iOS Chrome / iOS WebViews, older iOS), keeping the screen awake.
            await this.getNoSleep().enable();
            this.stopLowPowerKeepAwakeVideo();
        } catch (error) {
            // The Wake Lock API exists but rejected the request. This is what
            // happens on iOS Safari in Low Power Mode, where nosleep.js does not
            // fall back to its video technique. Play our own real looping video,
            // which keeps the iOS display awake even under Low Power Mode.
            this.startLowPowerKeepAwakeVideo();
            this.scheduleScreenWakeLockRetry();
            console.debug('Screen wake lock request failed; using video fallback.', error);
        } finally {
            this.isScreenWakeLockRequestInFlight = false;
        }
    }

    private releaseScreenWakeLock(): void {
        this.stopLowPowerKeepAwakeVideo();
        this.noSleep?.disable();
    }

    private scheduleScreenWakeLockRetry(): void {
        if (this.screenWakeLockRetryTimerId || !this.shouldKeepScreenAwake()) {
            return;
        }

        this.screenWakeLockRetryTimerId = setTimeout(() => {
            this.screenWakeLockRetryTimerId = null;
            void this.acquireScreenWakeLock();
        }, SCREEN_WAKE_LOCK_RETRY_MS);
    }

    private clearScreenWakeLockRetry(): void {
        if (!this.screenWakeLockRetryTimerId) {
            return;
        }

        clearTimeout(this.screenWakeLockRetryTimerId);
        this.screenWakeLockRetryTimerId = null;
    }

    private clearScreenWakeLockPrimeTimer(): void {
        if (!this.screenWakeLockPrimeTimerId) {
            return;
        }

        clearTimeout(this.screenWakeLockPrimeTimerId);
        this.screenWakeLockPrimeTimerId = null;
    }

    private startLowPowerKeepAwakeVideo(): void {
        if (this.lowPowerKeepAwakeVideo) {
            void this.lowPowerKeepAwakeVideo.play().catch(() => void 0);
            return;
        }

        const video = document.createElement('video');
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.autoplay = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('title', 'Manter tela ativa');
        video.style.position = 'fixed';
        video.style.width = '1px';
        video.style.height = '1px';
        video.style.opacity = '0';
        video.style.pointerEvents = 'none';
        video.style.left = '-1px';
        video.style.top = '-1px';

        this.addLowPowerKeepAwakeSource(video, 'webm', SCREEN_WAKE_LOCK_VIDEO_WEBM);
        this.addLowPowerKeepAwakeSource(video, 'mp4', SCREEN_WAKE_LOCK_VIDEO_MP4);

        document.body.appendChild(video);
        this.lowPowerKeepAwakeVideo = video;

        void video.play().catch((error) => {
            console.debug('Keep-awake video failed to play.', error);
        });
    }

    private addLowPowerKeepAwakeSource(
        video: HTMLVideoElement,
        type: 'webm' | 'mp4',
        dataUri: string,
    ): void {
        const source = document.createElement('source');
        source.src = dataUri;
        source.type = `video/${type}`;
        video.appendChild(source);
    }

    private stopLowPowerKeepAwakeVideo(): void {
        const video = this.lowPowerKeepAwakeVideo;

        if (!video) {
            return;
        }

        this.lowPowerKeepAwakeVideo = null;
        video.pause();

        while (video.firstChild) {
            video.removeChild(video.firstChild);
        }

        video.removeAttribute('src');
        video.load();
        video.remove();
    }

    private shouldKeepScreenAwake(): boolean {
        return (
            this.isScreenWakeLockEnabled
            && this.shouldContinueAudioRecording
            && !this.isPaused
            && document.visibilityState === 'visible'
        );
    }

    private hasLiveAudioTrack(stream: MediaStream | null): boolean {
        return !!stream?.getAudioTracks().some((track) => track.readyState === 'live');
    }

    private hasUsableAudioTrack(stream: MediaStream | null): boolean {
        return !!stream?.getAudioTracks().some(
            (track) => track.readyState === 'live' && track.enabled && !track.muted,
        );
    }

    private createAudioChunkTimestamp(): number {
        const now = Date.now();
        const timestamp = Math.max(now, this.lastAudioChunkTimestamp + 1);

        this.lastAudioChunkTimestamp = timestamp;

        return timestamp;
    }

    private getTotalPausedTimeMs(): number {
        if (!this.pausedStartedAtMs) {
            return this.totalPausedTimeMs;
        }

        return this.totalPausedTimeMs + Date.now() - this.pausedStartedAtMs;
    }

    private getAudioChunkMetadata(mediaRecorder: MediaRecorder): AudioChunkMetadata {
        const metadata = this.audioChunkMetadataByRecorder.get(mediaRecorder);

        if (metadata) {
            return metadata;
        }

        return this.createAudioChunkMetadata(mediaRecorder);
    }

    private createAudioChunkMetadata(mediaRecorder: MediaRecorder): AudioChunkMetadata {
        const metadata: AudioChunkMetadata = {
            isPaused: mediaRecorder.state === 'paused',
            timestamp: this.createAudioChunkTimestamp(),
            totalPausedTime: this.getTotalPausedTimeMs(),
        };

        this.audioChunkMetadataByRecorder.set(mediaRecorder, metadata);

        return metadata;
    }

    private scheduleAudioChunkRotation(mediaRecorder: MediaRecorder): void {
        this.clearAudioChunkTimer();

        this.audioChunkTimerId = setTimeout(() => {
            if (this.mediaRecorder !== mediaRecorder || mediaRecorder.state !== 'recording') {
                return;
            }

            void this.stopAudioRecorderForChunk(mediaRecorder);
        }, AUDIO_CHUNK_INTERVAL_MS);
    }

    private clearAudioChunkTimer(): void {
        if (!this.audioChunkTimerId) {
            return;
        }

        clearTimeout(this.audioChunkTimerId);
        this.audioChunkTimerId = null;
    }

    private stopAudioRecorderForChunk(mediaRecorder: MediaRecorder): Promise<void> {
        if (mediaRecorder.state === 'inactive') {
            return Promise.resolve();
        }

        this.createAudioChunkMetadata(mediaRecorder);

        const stopPromise = new Promise<void>((resolve) => {
            mediaRecorder.addEventListener('stop', () => resolve(), { once: true });
        });

        this.pendingAudioRecorderStops.push(stopPromise);

        void stopPromise.finally(() => {
            this.pendingAudioRecorderStops = this.pendingAudioRecorderStops.filter(
                (pendingStop) => pendingStop !== stopPromise,
            );
        });

        mediaRecorder.stop();

        return stopPromise;
    }

    private async waitForPendingAudioRecorderStops(): Promise<void> {
        while (this.pendingAudioRecorderStops.length) {
            await Promise.allSettled([...this.pendingAudioRecorderStops]);
        }
    }
}
